import { type FC, type Ref, type RefObject,useEffect } from "react";

export type Pt = { x: number; y: number };

export type Frame = {
  /** seconds since the loop started */
  t: number;
  /** head of the travelling wave, in `u` units */
  wave: number;
  /** radar hand angle, radians */
  sweep: number;
  /** 0…1 envelope that breathes the reduction ripple down */
  settle: number;
};

export type Painter = (pts: Pt[], a: number, f: Frame) => void;

export type LayerProps = { register: (paint: Painter) => void };

/** Registers a layer painter after its drawing refs exist. */
export function usePainter(register: LayerProps["register"], make: () => Painter): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => register(make()), [register]);
}

export type LayerMeta = {
  /** resting position of the point at `u` ∈ [0,1] */
  shape: (u: number) => Pt;
  /** true when the strand is a loop, drawn wrap-around so it never gaps */
  closed?: boolean;
  /** set false to skip the shared full-width strand, for layers that draw their own lines */
  strand?: boolean;
  /** set false when the layer draws its own marks instead of the shared dot field */
  dots?: boolean;
  /** vertical offset layered onto the resting position (scaled by presence) */
  offsetY?: (u: number, y: number, f: Frame) => number;
  /** hard lower boundary in SVG space; points and curve controls cannot cross it */
  floorY?: number;
  /** extra brightness for the point now at (x, y) (scaled by presence) */
  light?: (u: number, x: number, y: number, f: Frame) => number;
  /** scales the idle dot brightness while this animation shows (default 1) */
  fade?: number;
};

export type Layer = FC<LayerProps> & LayerMeta;

export const defineLayer = (draw: FC<LayerProps>, meta: LayerMeta): Layer => Object.assign(draw, meta);

export const kids = <K extends keyof SVGElementTagNameMap>(ref: RefObject<Element | null>, selector: K): SVGElementTagNameMap[K][] =>
  Array.from(ref.current?.querySelectorAll(selector) ?? []);

export const FIELD = { w: 1200, h: 480 } as const;
export const CX = FIELD.w / 2;
export const CY = FIELD.h / 2;
export const N = 160;
export const R_DOT = 2.1;
export const STEM_TOP = 110;
export const STEM_FLOOR = 452;

export const TAU = Math.PI * 2;
export const smoothstep = (u: number) => u * u * (3 - 2 * u);
export const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);
export const rowX = (u: number) => u * FIELD.w;
export const polar = (angle: number, r: number): Pt => ({
  x: CX + Math.cos(angle) * r,
  y: CY + Math.sin(angle) * r,
});
export const crest = (u: number, head: number) => Math.exp(-((u - head) ** 2) / 0.006);
/**
 * How far `hand` has swept *past* `dot`, 0…TAU. Near 0 right after the hand
 * crosses a dot, climbing toward TAU for dots it will not reach for a while,
 * so a decay over this reads as a comet trail behind the hand.
 */
export const trailing = (dot: number, hand: number) => (((hand - dot) % TAU) + TAU) % TAU;

export const frameAt = (t: number): Frame => ({
  t,
  wave: (t * 0.16) % 1.15,
  sweep: t * 1.1 - Math.PI / 2,
  settle: 0.55 + 0.45 * Math.cos(t * 0.9),
});

export const driftX = (i: number, t: number) => Math.sin(t * 0.55 + i * 0.19) * 3;
export const driftY = (i: number, t: number) => Math.cos(t * 0.5 + i * 0.19) * 3;
export const twinkle = (i: number, t: number) => 0.45 + 0.3 * Math.sin(t * 1.3 + i * 0.28);

const REPEL_RADIUS = 150;
const REPEL_PUSH = 52;

/** Shove a point away from the cursor. Mutates `p`. */
export function repel(p: Pt, mx: number, my: number): void {
  const dx = p.x - mx;
  const dy = p.y - my;
  const d2 = dx * dx + dy * dy;
  if (d2 >= REPEL_RADIUS * REPEL_RADIUS || d2 <= 0.1) return;
  const d = Math.sqrt(d2);
  const push = ((1 - d / REPEL_RADIUS) ** 2 * REPEL_PUSH) / d;
  p.x += dx * push;
  p.y += dy * push;
}

const fmt = (p: Pt) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`;

export const Strand = ({ ref, className, width = 1.2, opacity = 0 }: { ref?: Ref<SVGPathElement>; className?: string; width?: number; opacity?: number }) => (
  <path ref={ref} className={className} fill="none" stroke="currentColor" strokeWidth={width} strokeLinecap="round" opacity={opacity} />
);

export const AxisRule = ({ ref, y, width = 1.2, dash = "7 9" }: { ref?: Ref<SVGPathElement>; y?: number; width?: number; dash?: string }) => (
  <path
    ref={ref}
    d={y === undefined ? undefined : `M${rowX(0)},${y}L${rowX(1)},${y}`}
    fill="none"
    stroke="currentColor"
    strokeWidth={width}
    strokeDasharray={dash}
    strokeLinecap="round"
    opacity={0}
  />
);

export const setPath = (el: SVGPathElement | null, d: string, opacity: number) => {
  if (!el) return;
  el.setAttribute("d", d);
  el.setAttribute("opacity", opacity.toFixed(2));
};

export const ribbon = (top: Pt[], bottom: Pt[]) => `M${top.map(fmt).join(" L")} L${[...bottom].reverse().map(fmt).join(" L")} Z`;

/**
 * Catmull-Rom through the points, emitted as cubic béziers (C1-smooth).
 * `closed` wraps around and closes the path, so a loop never shows a gap.
 */
export function toPath(pts: Pt[], closed = false, floorY?: number): string {
  const points = floorY === undefined ? pts : pts.map((p) => ({ x: p.x, y: Math.min(p.y, floorY) }));
  const n = points.length;
  if (n < 2) return "";
  const loop = closed && n >= 3;
  const at = (i: number) => points[loop ? (i + n) % n : Math.min(Math.max(i, 0), n - 1)];
  const ctrl = (from: Pt, a: Pt, b: Pt) => {
    const point = { x: from.x + (a.x - b.x) / 6, y: from.y + (a.y - b.y) / 6 };
    if (floorY !== undefined) point.y = Math.min(point.y, floorY);
    return fmt(point);
  };

  let d = `M${fmt(points[0])}`;
  for (let i = 0; i < (loop ? n : n - 1); i++) {
    const [p0, p1, p2, p3] = [at(i - 1), at(i), at(i + 1), at(i + 2)];
    d += `C${ctrl(p1, p2, p0)} ${ctrl(p2, p1, p3)} ${fmt(p2)}`;
  }
  return loop ? `${d}Z` : d;
}
